import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TopBar from '../components/TopBar';
import SafeIcon from '../common/SafeIcon';
import { tracks } from '../data/mockData';

export default function Builder() {
  const [playlist, setPlaylist] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const addTrack = (track) => {
    if (!playlist.find(t => t.id === track.id)) {
      setPlaylist([...playlist, track]);
    }
  };

  const removeTrack = (id) => {
    setPlaylist(playlist.filter(t => t.id !== id));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 1500);
  };

  return (
    <div className="flex-1 overflow-y-auto w-full h-full bg-mesh-pattern pb-32">
      <TopBar title="Playlist Matrix" />
      
      <div className="p-6 max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
        
        {/* Left: Source Library */}
        <div className="space-y-6">
          <div className="glass-panel p-4 rounded-xl">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <SafeIcon icon="FiDatabase" className="text-aqua" /> System Library
            </h3>
            <div className="space-y-2">
              {tracks.map(track => (
                <div key={track.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-vinyl-700/50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <img src={track.cover} alt="" className="w-8 h-8 rounded object-cover" />
                    <div>
                      <p className="text-sm text-white font-medium">{track.title}</p>
                      <p className="text-xs text-gray-400">{track.artist}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => addTrack(track)}
                    className="w-8 h-8 rounded-full bg-vinyl-700 text-gray-300 hover:bg-plasma hover:text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  >
                    <SafeIcon icon="FiPlus" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Active Playlist */}
        <div className="space-y-6">
          <div className="glass-panel p-6 rounded-xl border-t-4 border-t-plasma flex flex-col h-[600px]">
            <div className="flex justify-between items-end mb-6">
              <div>
                <input 
                  type="text" 
                  defaultValue="New Sequence 01" 
                  className="bg-transparent text-2xl font-display font-bold text-white border-b border-transparent hover:border-vinyl-600 focus:border-plasma focus:outline-none transition-colors w-full mb-1"
                />
                <p className="text-sm text-gray-400">{playlist.length} tracks selected</p>
              </div>
              <button 
                onClick={handleSave}
                disabled={playlist.length === 0 || isSaving}
                className={`px-6 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all ${
                  playlist.length === 0 
                  ? 'bg-vinyl-700 text-gray-500 cursor-not-allowed'
                  : saved 
                    ? 'bg-green-500 text-white'
                    : 'bg-aqua text-vinyl-900 hover:bg-white glow-aqua'
                }`}
              >
                {isSaving ? <SafeIcon icon="FiLoader" className="animate-spin" /> : 
                 saved ? <SafeIcon icon="FiCheck" /> : <SafeIcon icon="FiSave" />}
                {isSaving ? 'Compiling...' : saved ? 'Saved' : 'Compile'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              <AnimatePresence>
                {playlist.length === 0 ? (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="h-full flex flex-col items-center justify-center text-gray-500 border-2 border-dashed border-vinyl-700 rounded-xl"
                  >
                    <SafeIcon icon="FiInbox" className="text-4xl mb-2 opacity-50" />
                    <p>Insert tracks to compile sequence</p>
                  </motion.div>
                ) : (
                  playlist.map((track, idx) => (
                    <motion.div 
                      key={track.id + idx}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="flex items-center justify-between p-3 bg-vinyl-700/30 border border-vinyl-600/50 rounded-lg group"
                    >
                      <div className="flex items-center gap-3 cursor-move">
                        <SafeIcon icon="FiMenu" className="text-gray-500 hover:text-white" />
                        <span className="text-plasma font-display text-xs w-4">{idx + 1}</span>
                        <img src={track.cover} alt="" className="w-8 h-8 rounded object-cover" />
                        <div>
                          <p className="text-sm text-white">{track.title}</p>
                          <p className="text-xs text-gray-400">{track.artist}</p>
                        </div>
                      </div>
                      <button onClick={() => removeTrack(track.id)} className="text-gray-500 hover:text-red-500 transition-colors hidden group-hover:block">
                        <SafeIcon icon="FiTrash2" />
                      </button>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}